// src/components/opportunity/OpportunityDocumentsCategory.tsx
//
// One category section inside the Opportunity Documents tab. Renders
// the collapsible header, the new-upload form (document_type + file
// input), and a per-document-type version list (active + archived).
//
// Documents are grouped by `document_type` so a single building quote
// with two active versions (v1 → v2) renders as one row with two
// pills, and a third upload auto-archives v1 (handled server-side).

import { useEffect, useMemo, useRef, useState } from "react";
import {
  OPPORTUNITY_DOC_ALLOWED_EXTENSIONS,
  OPPORTUNITY_DOC_HUMAN_MAX,
} from "@/components/opportunity/opportunityDocumentsConstants";
import type { OpportunityDocument } from "@/lib/opportunity/opportunityDocumentsClient";
import {
  listOpportunityDocuments,
  uploadOpportunityDocument,
} from "@/lib/opportunity/opportunityDocumentsClient";

export type OpportunityDocumentsCategoryProps = {
  opportunityId: string;
  category: string;
  label: string;
  helperText: string;
  documents: OpportunityDocument[];
  busy: boolean;
  onUploaded: () => void;
  readOnly?: boolean;
  requiredBeforeSigned?: boolean;
  /** Emphasize this slot when navigating from Generated → Upload signed. */
  highlighted?: boolean;
  /** Optional link back to a generated package when uploading the signed file. */
  sourceGeneratedOutputId?: string | null;
  suggestedDocumentType?: string | null;
};

export function OpportunityDocumentsCategorySection(props: OpportunityDocumentsCategoryProps) {
  const {
    opportunityId,
    category,
    label,
    helperText,
    documents,
    busy,
    onUploaded,
    readOnly,
    requiredBeforeSigned,
    highlighted,
    sourceGeneratedOutputId,
    suggestedDocumentType,
  } = props;
  const [open, setOpen] = useState(true);
  const [documentType, setDocumentType] = useState(suggestedDocumentType ?? "");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!highlighted) return;
    setOpen(true);
    if (suggestedDocumentType) setDocumentType(suggestedDocumentType);
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlighted, suggestedDocumentType]);

  const grouped = useMemo(() => groupByDocumentType(documents), [documents]);
  const activeCount = documents.filter((d) => d.is_active).length;

  async function handlePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!documentType.trim()) {
      setUploadError("Enter a document type first (e.g. Building Quote, Loan Application).");
      e.target.value = "";
      return;
    }
    setUploadError(null);
    setUploading(true);
    const r = await uploadOpportunityDocument({
      opportunityId,
      category,
      documentType: documentType.trim(),
      file,
      sourceGeneratedOutputId: sourceGeneratedOutputId ?? null,
    });
    setUploading(false);
    e.target.value = "";
    if (!r.ok) {
      setUploadError(r.error);
      return;
    }
    setDocumentType("");
    onUploaded();
  }

  return (
    <section
      ref={sectionRef}
      className={
        "rounded-2xl border bg-white dark:bg-zinc-900 " +
        (highlighted
          ? "border-indigo-400 ring-2 ring-indigo-200 dark:border-indigo-500 dark:ring-indigo-900"
          : "border-gray-200 dark:border-zinc-700")
      }
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div>
          <div className="font-semibold text-gray-900 dark:text-zinc-100 flex flex-wrap items-center gap-2">
            <span>
              {label}
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-zinc-400">
                {activeCount} active · {documents.length} total
              </span>
            </span>
            {requiredBeforeSigned ? (
              <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-900 dark:bg-amber-950/35 dark:text-amber-200">
                Required before Signed
              </span>
            ) : null}
          </div>
          <div className="text-xs text-gray-500 dark:text-zinc-400">{helperText}</div>
        </div>
        <span className="text-xs text-gray-500 dark:text-zinc-400">{open ? "Hide" : "Show"}</span>
      </button>
      {open ? (
        <div className="border-t border-gray-100 dark:border-zinc-800 px-4 py-3 space-y-3">
          {!readOnly ? (
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-zinc-300">
                Document type
              </label>
              <input
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                placeholder="e.g. Building Quote"
                disabled={busy || uploading}
                className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
              />
              <p className="text-[11px] text-gray-500 dark:text-zinc-400 mt-1">
                Uploads are limited to {OPPORTUNITY_DOC_HUMAN_MAX} and these formats:{" "}
                {OPPORTUNITY_DOC_ALLOWED_EXTENSIONS.join(", ")}. Max 2 active versions per
                document type — older versions are auto-archived.
              </p>
            </div>
            <label
              className={
                "inline-flex items-center justify-center px-3 py-2 rounded-lg border text-sm cursor-pointer " +
                (busy || uploading
                  ? "border-gray-200 text-gray-400 cursor-not-allowed dark:border-zinc-700 dark:text-zinc-500"
                  : "border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-500 dark:text-indigo-200 dark:hover:bg-indigo-950/30")
              }
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                className="hidden"
                onChange={(e) => void handlePick(e)}
                disabled={busy || uploading}
              />
              {uploading ? "Uploading…" : "Choose file"}
            </label>
          </div>
          ) : null}
          {uploadError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
              {uploadError}
            </div>
          ) : null}

          {grouped.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-zinc-400">
              No documents in this category yet.
            </p>
          ) : (
            <div className="space-y-2">
              {grouped.map((g) => (
                <DocumentTypeGroup
                  key={g.documentType}
                  opportunityId={opportunityId}
                  group={g}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}

function DocumentTypeGroup(props: { opportunityId: string; group: GroupedDocument }) {
  const { opportunityId, group } = props;
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);

  async function openDocument(documentId: string) {
    setOpeningId(documentId);
    setOpenError(null);
    const r = await listOpportunityDocuments({ opportunityId, signMedia: true });
    setOpeningId(null);
    if (!r.ok) {
      setOpenError(r.error);
      return;
    }
    const fresh = (r.data.documents ?? []).find((row) => row.id === documentId);
    if (!fresh?.signed_url) {
      setOpenError("Open link unavailable — try Refresh.");
      return;
    }
    window.open(fresh.signed_url, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800/60">
      <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
        {group.documentType}
      </div>
      {openError ? (
        <p className="mt-1 text-[11px] text-red-700 dark:text-red-300">{openError}</p>
      ) : null}
      <ul className="mt-1.5 space-y-1">
        {group.versions.map((d) => (
          <li
            key={d.id}
            className="flex items-center justify-between gap-3 text-xs"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={
                  "inline-flex items-center rounded-full px-1.5 py-0.5 font-medium " +
                  (d.is_active
                    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
                    : "bg-gray-200 text-gray-700 dark:bg-zinc-700 dark:text-zinc-300")
                }
              >
                v{d.version}
              </span>
              <span className="text-gray-700 dark:text-zinc-300 truncate">
                {d.display_name || d.object_path.split("/").pop()}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[11px] text-gray-500 dark:text-zinc-400 tabular-nums">
                {humanBytes(d.byte_size)}
              </span>
              <button
                type="button"
                className="text-indigo-700 hover:underline disabled:opacity-50 dark:text-indigo-300"
                disabled={openingId === d.id}
                onClick={() => void openDocument(d.id)}
              >
                {openingId === d.id ? "Opening…" : "Open"}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

type GroupedDocument = { documentType: string; versions: OpportunityDocument[] };

function groupByDocumentType(docs: OpportunityDocument[]): GroupedDocument[] {
  const map = new Map<string, OpportunityDocument[]>();
  for (const d of docs) {
    const key = d.document_type;
    const arr = map.get(key) ?? [];
    arr.push(d);
    map.set(key, arr);
  }
  const out: GroupedDocument[] = [];
  for (const [documentType, versions] of map) {
    versions.sort((a, b) => b.version - a.version);
    out.push({ documentType, versions });
  }
  out.sort((a, b) => a.documentType.localeCompare(b.documentType));
  return out;
}

function humanBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
